import React from 'react'
import cx from 'classnames'

import styles from './Icon.module.scss'

export * from './model'

// the shape of the icon data in react-icons-kit/feather and react-icons-kit/md
type SvgNode = {
  name: string
  attribs: Record<string, string>
  children?: SvgNode[]
}

type SvgIconData = {
  viewBox: string
  attribs?: Record<string, string>
  children: SvgNode[]
}

export type IconSource = string | SvgIconData

// stroke-width to strokeWidth
const toPropName = (key: string) =>
  key.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())

const toProps = (attribs: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(attribs).map(([key, value]) => [toPropName(key), value]),
  )

// The renderer of react-icons-kit, which uses defaultProps that React 18.3
// warns about. Elements drop their own fill and stroke and take the colour
// from the svg, except outlined ones, which keep a stroke in currentColor.
const renderNodes = (nodes: SvgNode[]): React.ReactNode[] =>
  nodes.map(({ name, attribs, children }, index) => {
    const own = Object.fromEntries(
      Object.entries(attribs).filter(
        ([key, value]) =>
          key !== 'fill' && key !== 'stroke' && value !== 'none',
      ),
    )
    const outline =
      attribs.fill === 'none'
        ? { fill: 'none', ...(attribs.stroke && { stroke: 'currentColor' }) }
        : {}
    return React.createElement(
      name,
      { key: index, ...toProps(own), ...outline },
      children ? renderNodes(children) : null,
    )
  })

const SvgIcon = ({
  icon,
  size,
  title,
}: {
  icon: SvgIconData
  size: number
  title?: string
}) => (
  <i style={{ display: 'inline-block' }} title={title}>
    <svg
      fill="currentColor"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      height={size}
      width={size}
      viewBox={icon.viewBox}
      {...toProps(icon.attribs ?? {})}
    >
      {title ? <title>{title}</title> : null}
      {renderNodes(icon.children)}
    </svg>
  </i>
)

type IconProps = {
  ariaLabel?: string
  className?: string
  icon: IconSource
  onClick?: React.MouseEventHandler<HTMLElement>
  size?: number
  style?: React.CSSProperties
  title?: string
}

const isAssetIcon = (icon: IconSource): icon is string =>
  typeof icon === 'string'

export const Icon = React.forwardRef<HTMLElement | HTMLImageElement, IconProps>(
  ({ ariaLabel, className, onClick, icon, size = 16, style, title }, ref) => {
    if (isAssetIcon(icon)) {
      return (
        <img
          alt={ariaLabel || title || ''}
          className={cx(styles.icon, styles.assetIcon, className)}
          height={size}
          onClick={onClick}
          ref={ref as React.Ref<HTMLImageElement>}
          src={icon}
          style={style}
          title={title}
          width={size}
        />
      )
    }

    return (
      <span
        aria-label={ariaLabel}
        className={cx(styles.icon, className)}
        onClick={onClick}
        ref={ref as React.Ref<HTMLSpanElement>}
        role="img"
        style={style}
        title={title}
      >
        <SvgIcon {...{ icon, size, title }} />
      </span>
    )
  },
)
